import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Table,
  Card,
  Button,
  Input,
  DatePicker,
  message,
  Select,
} from "antd";
import {
  FilePdfOutlined,
  SearchOutlined,
  ReloadOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import moment from "moment";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";

import "./adminReportPage.css";

const { RangePicker } = DatePicker;
const { Option } = Select;

const AdminReportPage = () => {
  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [maleMinAge, setMaleMinAge] = useState("");
  const [maleMaxAge, setMaleMaxAge] = useState("");
  const [femaleMinAge, setFemaleMinAge] = useState("");
  const [femaleMaxAge, setFemaleMaxAge] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [genderFilter, setGenderFilter] = useState("All");

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    try {
      const response = await axios.get("https://back.varankal.com/api/admin/reports");
      setReportData(response.data);
      setFilteredData(response.data);
    } catch (error) {
      console.error("Error fetching report data:", error);
      message.error("Failed to fetch report data.");
    }
  };

  const applyFilters = () => {
    let filtered = [...reportData];

    if (searchQuery.trim()) {
      filtered = filtered.filter((item) =>
        item.name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (dateRange && dateRange.length === 2) {
      filtered = filtered.filter((item) => {
        if (!item.createdAt) return false;
        const createdAt = moment(item.createdAt);
        return (
          createdAt.isSameOrAfter(dateRange[0], "day") &&
          createdAt.isSameOrBefore(dateRange[1], "day")
        );
      });
    }

    if (genderFilter !== "All") {
      filtered = filtered.filter(
        (item) => item.gender && item.gender.toLowerCase() === genderFilter.toLowerCase()
      );
    }

    filtered = filtered.filter((item) => {
      if (typeof item.age !== "number") return false; // Ignore if age not number
      if (!item.gender) return false;

      if (item.gender.toLowerCase() === "male") {
        if (maleMinAge && item.age < Number(maleMinAge)) return false;
        if (maleMaxAge && item.age > Number(maleMaxAge)) return false;
      } else if (item.gender.toLowerCase() === "female") {
        if (femaleMinAge && item.age < Number(femaleMinAge)) return false;
        if (femaleMaxAge && item.age > Number(femaleMaxAge)) return false;
      }
      return true;
    });

    setFilteredData(filtered);
  };

  const handleReset = () => {
    setMaleMinAge("");
    setMaleMaxAge("");
    setFemaleMinAge("");
    setFemaleMaxAge("");
    setDateRange(null);
    setSearchQuery("");
    setGenderFilter("All");
    setFilteredData(reportData);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const title = "Varankal Users";
    const textWidth = doc.getTextWidth(title);
    const x = (pageWidth - textWidth) / 2;
    doc.text(title, x, 15);

    autoTable(doc, {
      head: [["User ID", "Name", "Gender", "Age", "Partner Age Preference"]],
      body: filteredData.map((item) => [
        item.userId,
        item.name,
        item.gender,
        item.age,
        item.partnerAge,
      ]),
      startY: 25,
    });

    doc.save("admin_report.pdf");
  };

  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "User Report");
    XLSX.writeFile(workbook, "admin_report.xlsx");
  };

  const columns = [
    { title: "User ID", dataIndex: "userId", key: "userId" },
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Gender", dataIndex: "gender", key: "gender" },
    { title: "Age", dataIndex: "age", key: "age" },
    { title: "Partner Age Preference", dataIndex: "partnerAge", key: "partnerAge" },
  ];

  // Prepare data for bar chart
  const genderChartData = [
    {
      gender: "Male",
      count: filteredData.filter((item) => item.gender?.toLowerCase() === "male").length,
    },
    {
      gender: "Female",
      count: filteredData.filter((item) => item.gender?.toLowerCase() === "female").length,
    },
  ];

  // Custom gradient for bar fill
  const GradientBar = ({ fill }) => (
    <defs>
      <linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={fill} stopOpacity={0.8} />
        <stop offset="100%" stopColor={fill} stopOpacity={0.3} />
      </linearGradient>
    </defs>
  );

  return (
    <div
      className="admin-report-containerT44"
      style={{ padding: "20px", background: "#ffe6f0" }}
    >
      <h2 className="report-titleT44" style={{ color: "#cc3366" }}>
        User Report
      </h2>

      <Card
        className="filter-cardT44"
        style={{ marginBottom: 20, background: "#fff0f6" }}
      >
        <div
          className="filter-containerT44"
          style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}
        >
          <div>
            <h4 style={{ marginBottom: 4, color: "#cc3366" }}>Male Age Between</h4>
            <Input
              placeholder="Min"
              value={maleMinAge}
              onChange={(e) => setMaleMinAge(e.target.value.replace(/\D/g, ""))}
              style={{ width: 80, marginRight: 8 }}
            />
            <Input
              placeholder="Max"
              value={maleMaxAge}
              onChange={(e) => setMaleMaxAge(e.target.value.replace(/\D/g, ""))}
              style={{ width: 80 }}
            />
          </div>

          <div>
            <h4 style={{ marginBottom: 4, color: "#cc3366" }}>Female Age Between</h4>
            <Input
              placeholder="Min"
              value={femaleMinAge}
              onChange={(e) => setFemaleMinAge(e.target.value.replace(/\D/g, ""))}
              style={{ width: 80, marginRight: 8 }}
            />
            <Input
              placeholder="Max"
              value={femaleMaxAge}
              onChange={(e) => setFemaleMaxAge(e.target.value.replace(/\D/g, ""))}
              style={{ width: 80 }}
            />
          </div>

          <div>
            <h4 style={{ marginBottom: 4, color: "#cc3366" }}>Gender</h4>
            <Select
              value={genderFilter}
              onChange={(value) => setGenderFilter(value)}
              style={{ width: 120 }}
            >
              <Option value="All">All</Option>
              <Option value="Male">Male</Option>
              <Option value="Female">Female</Option>
            </Select>
          </div>

          <div>
            <h4 style={{ marginBottom: 4, color: "#cc3366" }}>Date Range</h4>
            <RangePicker
              onChange={(dates) => setDateRange(dates)}
              value={dateRange}
              style={{ minWidth: 240 }}
              allowClear
            />
          </div>

          <div style={{ minWidth: 200 }}>
            <h4 style={{ marginBottom: 4, color: "#cc3366" }}>Search Name</h4>
            <Input
              placeholder="Enter name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: 200 }}
            />
          </div>

          <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={applyFilters}
              style={{ backgroundColor: "#cc3366", borderColor: "#cc3366" }}
            >
              Apply
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              Reset
            </Button>
          </div>
        </div>
      </Card>

      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="userId"
        pagination={{ pageSize: 10 }}
        style={{ background: "#fff" }}
        locale={{ emptyText: "No data found" }}
      />

      <h3 style={{ color: "#cc3366", marginTop: 30, marginBottom: 10 }}>
        Gender Distribution
      </h3>
      <div
        style={{
          width: "100%",
          height: 300,
          backgroundColor: "#fff0f6",
          padding: 20,
          borderRadius: 12,
          boxShadow:
            "0 6px 12px rgba(204, 51, 102, 0.15), 0 2px 4px rgba(204, 51, 102, 0.1)",
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={genderChartData}
            margin={{ top: 15, right: 30, left: 0, bottom: 15 }}
            barCategoryGap="30%"
          >
            <CartesianGrid strokeDasharray="4 4" vertical={false} />
            <XAxis
              dataKey="gender"
              tick={{ fill: "#cc3366", fontWeight: "600", fontSize: 14 }}
              axisLine={{ stroke: "#cc3366" }}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fill: "#cc3366", fontWeight: "600", fontSize: 14 }}
              axisLine={{ stroke: "#cc3366" }}
            />
            <Tooltip
              contentStyle={{ backgroundColor: "#fff", borderRadius: 8 }}
              itemStyle={{ color: "#cc3366", fontWeight: "600" }}
            />
            <Legend
              verticalAlign="top"
              height={36}
              wrapperStyle={{ color: "#cc3366", fontWeight: "700" }}
            />
            <Bar
              dataKey="count"
              radius={[12, 12, 0, 0]}
              fill="url(#colorUv)"
              barSize={50}
            >
              <defs>
                <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#cc3366" stopOpacity={0.8} />
                  <stop offset="100%" stopColor="#cc3366" stopOpacity={0.3} />
                </linearGradient>
              </defs>
              {genderChartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={index === 0 ? "#cc3366" : "#ff99bb"}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div
        style={{
          marginTop: 30,
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
        }}
      >
        <Button
          icon={<FilePdfOutlined />}
          onClick={handleDownloadPDF}
          style={{ backgroundColor: "#cc3366", color: "#fff", border: "none" }}
        >
          Export PDF
        </Button>
        <Button
          icon={<FileExcelOutlined />}
          onClick={handleDownloadExcel}
          style={{ backgroundColor: "#4caf50", color: "#fff", border: "none" }}
        >
          Export Excel
        </Button>
      </div>
    </div>
  );
};

export default AdminReportPage;
